/**
 * Scroll bridge for web.
 *
 * React Navigation gives each screen `position: absolute`, taking it out of the
 * normal page flow. As a result the React root (`#root`) doesn't grow to fit a
 * screen's content, so the page's scrollbar can't reach anything taller than the
 * viewport.
 *
 * The fix: a screen reports its content height on the `maincontentlayout` window
 * event, and the page that hosts the app resizes `#root` to match. This module is
 * both ends of that bridge — two ways to report a height
 * (`handleMainContentLayout`, `usePublishMainContentHeight`) and one way to read
 * it (`useMainContentLayout`).
 */

import { type RefObject, useEffect, useState } from "react";
import type { LayoutChangeEvent, LayoutRectangle } from "react-native";

const MAIN_CONTENT_LAYOUT_FALLBACK_TIMEOUT = 250;

declare global {
  interface GlobalEventHandlersEventMap {
    maincontentlayout: MainContentLayoutEvent;
  }
}

/**
 * Reports a view's height from its `onLayout`. Fine when the view's box is its
 * full height; if a flex parent stretches or clips it, use
 * `usePublishMainContentHeight` instead (see why there).
 */
export function handleMainContentLayout(layoutEvent: LayoutChangeEvent): void {
  window.dispatchEvent(new MainContentLayoutEvent(layoutEvent.nativeEvent.layout));
}

/**
 * Reports a content element's height and keeps it up to date as the element
 * resizes, so `#root` always tracks the full content. Prefer this over
 * `handleMainContentLayout` for tall or growing content.
 *
 * Why not just `onLayout`: when a flex parent constrains the element, `onLayout`
 * measures only the visible part (roughly the viewport height), so `#root` never
 * grows and tall content gets clipped. Reading `scrollHeight` from a
 * ResizeObserver gives the true content height instead.
 *
 * @param ref - the content element to measure.
 * @param enabled - report only when true; pass false to opt out (e.g. native, or
 *   a host that already sizes itself).
 * @param options.rootElementId - host mount node the page sizes from the event.
 *   Defaults to `"root"` (profile / viewjob). Resume-generation uses
 *   `"resume-generation-root"`.
 */
export function usePublishMainContentHeight(
  ref: RefObject<unknown>,
  enabled: boolean,
  options?: { rootElementId?: string },
): void {
  const rootElementId = options?.rootElementId ?? "root";
  useEffect(() => {
    if (!enabled || typeof ResizeObserver === "undefined" || typeof document === "undefined") {
      return;
    }
    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- RN-5783 baseline: pre-existing cast; migrate to zod/proper types (see docs/type-assertions.md)
    const node = ref.current as HTMLElement | null;
    const rootEl = document.getElementById(rootElementId);
    if (!node || !rootEl) {
      return;
    }
    let frame = 0;
    const publish = () => {
      cancelAnimationFrame(frame);
      // Defer to the next frame so we read the post-layout scrollHeight and
      // coalesce bursts of ResizeObserver callbacks.
      frame = requestAnimationFrame(() => {
        window.dispatchEvent(
          new MainContentLayoutEvent({
            x: 0,
            y: 0,
            width: rootEl.scrollWidth,
            // Measure the node, not rootEl: resizing rootEl would
            // otherwise trigger another measurement (a feedback loop).
            height: node.scrollHeight,
          }),
        );
      });
    };
    const observer = new ResizeObserver(publish);
    observer.observe(node);
    publish();
    return () => {
      observer.disconnect();
      cancelAnimationFrame(frame);
    };
  }, [ref, enabled, rootElementId]);
}

/**
 * Returns the latest reported content size, or null until the first event. For
 * React code that needs the size; the host page reads the same event to size
 * `#root`.
 */
export function useMainContentLayout(): LayoutRectangle | null {
  const [layout, setLayout] = useState<LayoutRectangle | null>(null);
  useEffect(() => {
    const handler = (layoutEvent: MainContentLayoutEvent) => {
      setLayout(layoutEvent);
    };
    window.addEventListener("maincontentlayout", handler);
    return () => window.removeEventListener("maincontentlayout", handler);
  }, []);

  return layout;
}

/**
 * Runs work after the full-page host has received a published content height
 * and had two frames to apply it. The timeout supports hosts that do not use
 * the main-content layout bridge.
 */
export function scheduleAfterMainContentLayout(callback: () => void): () => void {
  let firstFrame: number | undefined;
  let secondFrame: number | undefined;
  let hasScheduledCallback = false;

  const runAfterLayout = () => {
    if (hasScheduledCallback) return;
    hasScheduledCallback = true;
    window.removeEventListener("maincontentlayout", runAfterLayout);
    clearTimeout(fallbackTimer);

    firstFrame = requestAnimationFrame(() => {
      secondFrame = requestAnimationFrame(callback);
    });
  };

  window.addEventListener("maincontentlayout", runAfterLayout, { once: true });
  const fallbackTimer = setTimeout(runAfterLayout, MAIN_CONTENT_LAYOUT_FALLBACK_TIMEOUT);

  return () => {
    window.removeEventListener("maincontentlayout", runAfterLayout);
    clearTimeout(fallbackTimer);
    if (firstFrame !== undefined) cancelAnimationFrame(firstFrame);
    if (secondFrame !== undefined) cancelAnimationFrame(secondFrame);
  };
}

/** Payload for the `maincontentlayout` event: the published content rectangle. */
class MainContentLayoutEvent extends Event implements LayoutRectangle {
  x: number;
  y: number;
  width: number;
  height: number;

  constructor(layout: LayoutRectangle) {
    super("maincontentlayout", { bubbles: true, composed: true });
    this.x = layout.x;
    this.y = layout.y;
    this.height = layout.height;
    this.width = layout.width;
  }
}
